import type { SampleConfigurationTypeKey } from '../../../Generated/SampleConfigurationType.js'
import { SampleConfigurationTypes } from '../../../Generated/SampleConfigurationType.js'
import { describeSampleConfiguration } from '../../../Util/sequencingPlatform.js'
import { type JSX } from 'react'
import { BeakerIcon, DocumentTextIcon, UsersIcon } from '@heroicons/react/24/outline'
import type { BaseModalProps } from '../../Modal/BaseModal.js'
import { BaseModal } from '../../Modal/BaseModal.js'

export interface SampleConfigurationTypeModalProps {
  initial?: SampleConfigurationTypeKey
}

const bullets: Record<SampleConfigurationTypeKey, { icon: JSX.Element; text: string }[]> = {
  SINGLE_SAMPLE_PAIRS: [
    { icon: <DocumentTextIcon className="w-4 h-4 inline-block mr-1" />, text: '2 .fastq.gz files / sample' },
    { icon: <UsersIcon className="w-4 h-4 inline-block mr-1" />, text: '1 pair = 1 sample' },
    { icon: <BeakerIcon className="w-4 h-4 inline-block mr-1" />, text: 'Primers are known' }
  ],
  COMBINED_SAMPLE_PAIRS: [
    { icon: <DocumentTextIcon className="w-4 h-4 inline-block mr-1" />, text: '2 .fastq.gz files / x samples' },
    { icon: <UsersIcon className="w-4 h-4 inline-block mr-1" />, text: '1 pair = x samples' },
    { icon: <BeakerIcon className="w-4 h-4 inline-block mr-1" />, text: 'Primers are known' }
  ],
  MERGED_AND_TRIMMED_SAMPLES: [
    { icon: <DocumentTextIcon className="w-4 h-4 inline-block mr-1" />, text: '1 .fastq.gz file / sample' },
    { icon: <UsersIcon className="w-4 h-4 inline-block mr-1" />, text: '1 file = 1 sample' },
    { icon: <BeakerIcon className="w-4 h-4 inline-block mr-1" />, text: 'Primers are unknown' }
  ],
  MERGED_SAMPLES: [
    { icon: <DocumentTextIcon className="w-4 h-4 inline-block mr-1" />, text: '1 .fastq.gz file / sample' },
    { icon: <UsersIcon className="w-4 h-4 inline-block mr-1" />, text: '1 file = 1 sample' },
    { icon: <BeakerIcon className="w-4 h-4 inline-block mr-1" />, text: 'Primers are known' }
  ],
  TRIMMED_SAMPLES: [
    { icon: <DocumentTextIcon className="w-4 h-4 inline-block mr-1" />, text: '2 .fastq.gz files / sample' },
    { icon: <UsersIcon className="w-4 h-4 inline-block mr-1" />, text: '1 pair = 1 sample' },
    { icon: <BeakerIcon className="w-4 h-4 inline-block mr-1" />, text: 'Primers are unknown' }
  ]
}

export const SampleConfigurationTypeModal = ({
  isOpen,
  initial,
  onClose
}: SampleConfigurationTypeModalProps & BaseModalProps<SampleConfigurationTypeKey | undefined>) => {
  return (
    <BaseModal isOpen={isOpen} onClose={onClose}>
      <div className="modal-box relative w-fit max-w-[80vw] max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">Choose Your Sample Configuration Type</h2>
        <div className="flex flex-wrap justify-center gap-4">
          {Object.entries(SampleConfigurationTypes).map(([key, { label }]) => (
            <div
              key={key}
              className={`border-2 rounded-xl shadow-sm p-4 max-w-xs h-full flex flex-col justify-between cursor-pointer transition-all ${key === initial ? 'border-primary bg-primary/10' : 'hover:border-primary'}`}
              onClick={() => onClose(key as SampleConfigurationTypeKey)}
            >
              <div className={'h-32'}>
                <h3 className="text-lg font-semibold mb-2">{label}</h3>
                <p className="text-sm text-gray-600 mb-4 min-h-[64px]">
                  {describeSampleConfiguration(key as SampleConfigurationTypeKey, null)}
                </p>
              </div>
              <ul className="text-sm text-bold text-primary-contrast space-y-1 mt-auto">
                {bullets[key as SampleConfigurationTypeKey].map((item, index) => (
                  <li key={index} className="flex items-start">
                    {item.icon}
                    <span>{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="modal-action">
          <button className="btn btn-ghost mr-2" onClick={() => onClose()}>
            Cancel
          </button>
        </div>
      </div>
    </BaseModal>
  )
}
